/**
 * Phase 7: Case Status Tracking & USCIS Receipt Number Management
 *
 * Full-screen modal panel that lets users:
 *  - Enter / update their USCIS receipt number
 *  - View current case status with icon + color coding
 *  - Add manual status updates (e.g. "RFE Issued", "Approved")
 *  - Browse full status history timeline
 *  - Add personal notes
 *  - Jump to USCIS e-Request case status check page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw Object.assign(
      new Error(data.detail?.message || data.detail || 'Request failed'),
      { data, status: res.status }
    );
  return data;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'received',               label: '📬 Case Received' },
  { value: 'initial_review',         label: '🔍 Initial Review' },
  { value: 'biometrics_scheduled',   label: '📅 Biometrics Scheduled' },
  { value: 'biometrics_completed',   label: '✅ Biometrics Completed' },
  { value: 'fingerprints_scheduled', label: '📅 Fingerprints Scheduled' },
  { value: 'fingerprints_completed', label: '✅ Fingerprints Completed' },
  { value: 'interview_scheduled',    label: '📅 Interview Scheduled' },
  { value: 'interview_completed',    label: '✅ Interview Completed' },
  { value: 'rfe_issued',             label: '⚠️ RFE Issued' },
  { value: 'rfe_response_received',  label: '📥 RFE Response Received' },
  { value: 'decision_pending',       label: '⏳ Decision Pending' },
  { value: 'approved',               label: '🎉 Approved' },
  { value: 'denied',                 label: '❌ Denied' },
  { value: 'withdrawn',              label: '↩️ Withdrawn' },
  { value: 'transferred',            label: '🔄 Transferred' },
  { value: 'appeal_filed',           label: '⚖️ Appeal Filed' },
];

const SERVICE_CENTERS = [
  'Nebraska', 'Texas', 'California', 'Vermont', 'Potomac',
  'National Benefits Center',
];

const COLOR_MAP = {
  blue:   { bg: '#EFF6FF', border: '#3B82F6', text: '#1D4ED8', badge: '#DBEAFE' },
  green:  { bg: '#F0FDF4', border: '#22C55E', text: '#166534', badge: '#DCFCE7' },
  yellow: { bg: '#FEFCE8', border: '#EAB308', text: '#854D0E', badge: '#FEF9C3' },
  orange: { bg: '#FFF7ED', border: '#F97316', text: '#C2410C', badge: '#FFEDD5' },
  red:    { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B', badge: '#FEE2E2' },
  gray:   { bg: '#F9FAFB', border: '#9CA3AF', text: '#374151', badge: '#F3F4F6' },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusBadge = ({ status, label, color }) => {
  const colors = COLOR_MAP[color] || COLOR_MAP.gray;
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold"
      style={{ backgroundColor: colors.badge, color: colors.text, border: `1px solid ${colors.border}` }}
    >
      {label}
    </span>
  );
};

const ProcessingBar = ({ daysFiled, minDays, maxDays }) => {
  if (!minDays || !maxDays || daysFiled === null || daysFiled === undefined) return null;

  const pct = Math.min(100, Math.round((daysFiled / maxDays) * 100));
  const color = pct >= 100 ? '#EF4444' : pct >= 75 ? '#F97316' : '#22C55E';

  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Day {daysFiled} of {minDays}–{maxDays} expected</span>
        <span style={{ color }}>{pct}% through window</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const TimelineEntry = ({ entry, isLatest }) => {
  const colors = COLOR_MAP[entry.status_color] || COLOR_MAP.gray;
  return (
    <div className="flex gap-3">
      {/* connector */}
      <div className="flex flex-col items-center">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-base flex-shrink-0"
          style={{ backgroundColor: colors.badge, border: `2px solid ${colors.border}` }}
        >
          {entry.status_icon || '📋'}
        </div>
        {!isLatest && <div className="w-0.5 bg-gray-200 flex-1 mt-1" style={{ minHeight: 20 }} />}
      </div>
      {/* content */}
      <div className="pb-4 flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-800 text-sm">{entry.status_label}</span>
          {entry.source && entry.source !== 'manual' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              {entry.source}
            </span>
          )}
        </div>
        {entry.description && (
          <p className="text-sm text-gray-600 mt-0.5">{entry.description}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {entry.status_date ? new Date(entry.status_date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
          }) : '—'}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CaseStatusPanel = ({ pathwayId, onClose }) => {
  const [caseStatus, setCaseStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'update' | 'history' | 'notes'

  // Receipt number form
  const [receiptInput, setReceiptInput] = useState('');
  const [serviceCenter, setServiceCenter] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptMsg, setReceiptMsg] = useState(null);

  // Status update form
  const [updateStatus, setUpdateStatus] = useState('received');
  const [updateDate, setUpdateDate] = useState(new Date().toISOString().split('T')[0]);
  const [updateDescription, setUpdateDescription] = useState('');
  const [updateSaving, setUpdateSaving] = useState(false);
  const [updateMsg, setUpdateMsg] = useState(null);

  // Notes
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesMsg, setNotesMsg] = useState(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/case-status/${pathwayId}`);
      setCaseStatus(data);
      setReceiptInput(data.receipt_number || '');
      setServiceCenter(data.service_center || '');
      setReceiptDate(data.receipt_date ? data.receipt_date.split('T')[0] : '');
      setNotes(data.notes || '');
    } catch (err) {
      if (err.status === 404) {
        // Try to initialize
        try {
          await apiFetch(`/api/case-status/${pathwayId}`, {
            method: 'POST',
            body: JSON.stringify({}),
          });
          const data = await apiFetch(`/api/case-status/${pathwayId}`);
          setCaseStatus(data);
        } catch (initErr) {
          setError(initErr.message || 'Failed to initialize case status.');
        }
      } else {
        setError(err.message || 'Failed to load case status.');
      }
    } finally {
      setLoading(false);
    }
  }, [pathwayId]);

  const fetchHistory = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/case-status/${pathwayId}/history`);
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    }
  }, [pathwayId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleSaveReceipt = async () => {
    if (!receiptInput.trim()) return;
    setReceiptSaving(true);
    setReceiptMsg(null);
    try {
      const data = await apiFetch(`/api/case-status/${pathwayId}/receipt`, {
        method: 'PATCH',
        body: JSON.stringify({
          receipt_number: receiptInput.trim(),
          service_center: serviceCenter || null,
          receipt_date: receiptDate || null,
        }),
      });
      setCaseStatus(data.case_status);
      setReceiptMsg({ type: 'success', text: `Receipt number ${data.case_status.receipt_number} saved!` });
    } catch (err) {
      setReceiptMsg({ type: 'error', text: err.message || 'Failed to save receipt number.' });
    } finally {
      setReceiptSaving(false);
    }
  };

  const handleAddUpdate = async () => {
    if (!updateStatus || !updateDate) return;
    setUpdateSaving(true);
    setUpdateMsg(null);
    try {
      const data = await apiFetch(`/api/case-status/${pathwayId}/update`, {
        method: 'POST',
        body: JSON.stringify({
          status: updateStatus,
          status_date: updateDate,
          description: updateDescription || null,
        }),
      });
      setCaseStatus(data.case_status);
      setUpdateMsg({ type: 'success', text: 'Status updated successfully!' });
      setUpdateDescription('');
      // Refresh history if visible
      if (activeTab === 'history') fetchHistory();
    } catch (err) {
      setUpdateMsg({ type: 'error', text: err.message || 'Failed to update status.' });
    } finally {
      setUpdateSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setNotesSaving(true);
    setNotesMsg(null);
    try {
      const data = await apiFetch(`/api/case-status/${pathwayId}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ notes }),
      });
      setCaseStatus(data.case_status);
      setNotesMsg({ type: 'success', text: 'Notes saved!' });
    } catch (err) {
      setNotesMsg({ type: 'error', text: err.message || 'Failed to save notes.' });
    } finally {
      setNotesSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const colors = caseStatus
    ? COLOR_MAP[caseStatus.status_color] || COLOR_MAP.gray
    : COLOR_MAP.gray;

  const pathwayLabel = pathwayId
    ?.split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'update',   label: '✏️ Add Update' },
    { id: 'history',  label: '📅 History' },
    { id: 'notes',    label: '📝 Notes' },
  ];

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full mx-4">
          <div className="text-5xl mb-4 animate-pulse">📊</div>
          <p className="text-gray-600 font-medium">Loading case status…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
          <div className="text-center mb-4">
            <div className="text-5xl mb-3">⚠️</div>
            <h3 className="text-lg font-bold text-gray-800">Unable to Load Case Status</h3>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
            <p className="text-xs text-gray-400 mt-1">Submit your workflow first to enable case tracking.</p>
          </div>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg font-semibold text-white"
            style={{ backgroundColor: '#1B1B1E' }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-60 overflow-y-auto pt-6 pb-10 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">

        {/* ── Header ── */}
        <div
          className="rounded-t-2xl px-6 py-5"
          style={{ backgroundColor: '#1B1B1E' }}
        >
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                📊 Case Status Tracker
              </h2>
              <p className="text-sm mt-1" style={{ color: '#C6A664' }}>
                {pathwayLabel} — USCIS Receipt &amp; Status Management
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors text-2xl leading-none ml-4"
              title="Close"
            >
              ×
            </button>
          </div>

          {/* Receipt number display */}
          {caseStatus?.receipt_number && (
            <div
              className="mt-4 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3"
              style={{ backgroundColor: 'rgba(198,166,100,0.15)', border: '1px solid rgba(198,166,100,0.3)' }}
            >
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Receipt Number</p>
                <p className="text-lg font-mono font-bold" style={{ color: '#C6A664' }}>
                  {caseStatus.receipt_number}
                </p>
              </div>
              {caseStatus.service_center && (
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-0.5">Service Center</p>
                  <p className="text-sm font-semibold text-white">{caseStatus.service_center}</p>
                </div>
              )}
              {caseStatus.uscis_check_url && (
                <a
                  href={caseStatus.uscis_check_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                  style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                >
                  Check on USCIS.gov ↗
                </a>
              )}
            </div>
          )}
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-gray-200 px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="p-6">

          {/* ====== OVERVIEW TAB ====== */}
          {activeTab === 'overview' && caseStatus && (
            <div className="space-y-5">

              {/* Current status card */}
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{caseStatus.status_icon || '📋'}</span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: colors.text }}>
                      Current Status
                    </p>
                    <p className="text-lg font-bold" style={{ color: colors.text }}>
                      {caseStatus.current_status_label}
                    </p>
                  </div>
                  {caseStatus.is_overdue && (
                    <span className="ml-auto text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-700">
                      ⏰ Overdue
                    </span>
                  )}
                </div>
                {caseStatus.status_description && (
                  <p className="text-sm mt-1" style={{ color: colors.text, opacity: 0.8 }}>
                    {caseStatus.status_description}
                  </p>
                )}
                {caseStatus.status_description_default && !caseStatus.status_description && (
                  <p className="text-sm mt-1 text-gray-500">
                    {caseStatus.status_description_default}
                  </p>
                )}
                {caseStatus.status_date && (
                  <p className="text-xs text-gray-400 mt-2">
                    Status date:{' '}
                    {new Date(caseStatus.status_date).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </p>
                )}
              </div>

              {/* Processing timeline */}
              {(caseStatus.expected_processing_time_min_days || caseStatus.days_since_filing) && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Processing Timeline</h4>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    {caseStatus.days_since_filing !== null && caseStatus.days_since_filing !== undefined && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-2xl font-bold text-gray-800">{caseStatus.days_since_filing}</p>
                        <p className="text-xs text-gray-500">Days since filing</p>
                      </div>
                    )}
                    {caseStatus.days_since_receipt !== null && caseStatus.days_since_receipt !== undefined && (
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-2xl font-bold text-gray-800">{caseStatus.days_since_receipt}</p>
                        <p className="text-xs text-gray-500">Days since receipt</p>
                      </div>
                    )}
                  </div>
                  <ProcessingBar
                    daysFiled={caseStatus.days_since_receipt || caseStatus.days_since_filing}
                    minDays={caseStatus.expected_processing_time_min_days}
                    maxDays={caseStatus.expected_processing_time_max_days}
                  />
                  {caseStatus.expected_processing_time_min_days && (
                    <p className="text-xs text-gray-400 mt-2 text-center">
                      USCIS typical processing: {Math.round(caseStatus.expected_processing_time_min_days / 30)}–
                      {Math.round(caseStatus.expected_processing_time_max_days / 30)} months
                    </p>
                  )}
                </div>
              )}

              {/* Receipt number entry (if not set) */}
              {!caseStatus.receipt_number && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                    🔢 Enter Your USCIS Receipt Number
                  </h4>
                  <p className="text-xs text-yellow-700 mb-3">
                    Once USCIS receives your petition, they will mail a receipt notice (I-797) with your
                    receipt number (e.g. SRC2390012345). Enter it here to track your case.
                  </p>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg text-sm font-mono uppercase
                               focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                    placeholder="e.g. SRC2390012345"
                    value={receiptInput}
                    onChange={(e) => setReceiptInput(e.target.value.toUpperCase())}
                  />
                  {receiptMsg && (
                    <p className={`text-xs mt-2 ${receiptMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {receiptMsg.text}
                    </p>
                  )}
                  <button
                    onClick={handleSaveReceipt}
                    disabled={receiptSaving || !receiptInput.trim()}
                    className="mt-3 w-full py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                  >
                    {receiptSaving ? 'Saving…' : 'Save Receipt Number'}
                  </button>
                </div>
              )}

              {/* Key dates */}
              {(caseStatus.filing_date || caseStatus.receipt_date || caseStatus.priority_date) && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Dates</h4>
                  <dl className="space-y-2">
                    {caseStatus.filing_date && (
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-500">Filing Date</dt>
                        <dd className="font-medium text-gray-800">
                          {new Date(caseStatus.filing_date).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </dd>
                      </div>
                    )}
                    {caseStatus.receipt_date && (
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-500">Receipt Date</dt>
                        <dd className="font-medium text-gray-800">
                          {new Date(caseStatus.receipt_date).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </dd>
                      </div>
                    )}
                    {caseStatus.priority_date && (
                      <div className="flex justify-between text-sm">
                        <dt className="text-gray-500">Priority Date</dt>
                        <dd className="font-medium text-gray-800">
                          {new Date(caseStatus.priority_date).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveTab('update')}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                  style={{ backgroundColor: '#1B1B1E', color: '#FAFAF8' }}
                >
                  ✏️ Update Status
                </button>
                <button
                  onClick={() => { setActiveTab('history'); fetchHistory(); }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  📅 View History ({caseStatus.history_count || 0})
                </button>
                {caseStatus.uscis_check_url && (
                  <a
                    href={caseStatus.uscis_check_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold border text-center transition-colors"
                    style={{ borderColor: '#C6A664', color: '#92400E', backgroundColor: '#FFFBEB' }}
                  >
                    🌐 Check on USCIS
                  </a>
                )}
              </div>
            </div>
          )}

          {/* ====== RECEIPT NUMBER TAB (embedded in update) ====== */}

          {/* ====== ADD UPDATE TAB ====== */}
          {activeTab === 'update' && (
            <div className="space-y-5">

              {/* Update receipt number section */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">🔢 Receipt Number</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      USCIS Receipt Number
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase
                                 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                      placeholder="e.g. SRC2390012345"
                      value={receiptInput}
                      onChange={(e) => setReceiptInput(e.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Service Center
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                   focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                        value={serviceCenter}
                        onChange={(e) => setServiceCenter(e.target.value)}
                      >
                        <option value="">Auto-detect</option>
                        {SERVICE_CENTERS.map((sc) => (
                          <option key={sc} value={sc}>{sc}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Receipt Date
                      </label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                   focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                        value={receiptDate}
                        onChange={(e) => setReceiptDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {receiptMsg && (
                  <p className={`text-xs mt-2 ${receiptMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {receiptMsg.text}
                  </p>
                )}
                <button
                  onClick={handleSaveReceipt}
                  disabled={receiptSaving || !receiptInput.trim()}
                  className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                >
                  {receiptSaving ? 'Saving…' : 'Save Receipt Number'}
                </button>
              </div>

              {/* Status update form */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">📋 Record Status Update</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                      value={updateStatus}
                      onChange={(e) => setUpdateStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Status Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                      value={updateDate}
                      onChange={(e) => setUpdateDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
                                 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                      rows={3}
                      placeholder="e.g. Received I-797 receipt notice in mail…"
                      value={updateDescription}
                      onChange={(e) => setUpdateDescription(e.target.value)}
                    />
                  </div>
                </div>
                {updateMsg && (
                  <p className={`text-xs mt-2 ${updateMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {updateMsg.text}
                  </p>
                )}
                <button
                  onClick={handleAddUpdate}
                  disabled={updateSaving || !updateStatus || !updateDate}
                  className="mt-3 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 text-white"
                  style={{ backgroundColor: '#1B1B1E' }}
                >
                  {updateSaving ? 'Saving…' : 'Record Status Update'}
                </button>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Status updates here are tracked locally for your reference.
                  Always verify your official case status on{' '}
                  <a
                    href="https://egov.uscis.gov/casestatus/landing.do"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold"
                  >
                    USCIS.gov
                  </a>.
                  This is not legal advice.
                </p>
              </div>
            </div>
          )}

          {/* ====== HISTORY TAB ====== */}
          {activeTab === 'history' && (
            <div>
              {history.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-5xl mb-3">📭</div>
                  <p className="text-gray-500 font-medium">No status updates yet.</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Use the "Add Update" tab to record your first status change.
                  </p>
                </div>
              ) : (
                <div className="space-y-0">
                  {history.map((entry, idx) => (
                    <TimelineEntry
                      key={entry.id || idx}
                      entry={entry}
                      isLatest={idx === history.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ====== NOTES TAB ====== */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Personal Case Notes
                </label>
                <p className="text-xs text-gray-400 mb-3">
                  Track attorney advice, reminders, important communications, and next steps.
                  Notes are private to your account.
                </p>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm resize-vertical
                             focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white"
                  rows={10}
                  placeholder={
                    `Examples:\n` +
                    `• Received I-797 receipt notice on Jan 15\n` +
                    `• Attorney recommended filing premium processing\n` +
                    `• RFE response due by March 22\n` +
                    `• Called USCIS inquiry line: case in initial review`
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              {notesMsg && (
                <p className={`text-sm ${notesMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {notesMsg.text}
                </p>
              )}
              <button
                onClick={handleSaveNotes}
                disabled={notesSaving}
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#1B1B1E' }}
              >
                {notesSaving ? 'Saving…' : '💾 Save Notes'}
              </button>
            </div>
          )}

        </div>

        {/* ── Footer disclaimer ── */}
        <div className="px-6 pb-5">
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-500 text-center">
              Case status tracking is for informational purposes only. Always verify your official
              case status directly at{' '}
              <a
                href="https://egov.uscis.gov/casestatus/landing.do"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-600"
              >
                USCIS Case Status Online
              </a>
              . This is not legal advice.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CaseStatusPanel;
